import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import './ReferralTree.css';

const RANK_ICONS = { bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '👑' };

// Un nœud + ses enfants (lazy-loadés au premier dépli, puis mis en cache
// côté client — jamais rechargés/recalculés une fois obtenus).
const TreeNode = ({ node, depth, expandedIds, childrenMap, loadingIds, onToggle, highlightId, nodeRefs }) => {
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.directCount > 0;
  const isLoading = loadingIds.has(node.id);
  const loadedChildren = childrenMap[node.id];
  const isHighlighted = highlightId === node.id;

  return (
    <div className="ref-tree-node">
      <div
        ref={(el) => { nodeRefs.current[node.id] = el; }}
        className={`ref-tree-row ${isHighlighted ? 'highlight' : ''} ${!hasChildren ? 'leaf' : ''}`}
        onClick={() => hasChildren && onToggle(node.id)}
      >
        <span className={`ref-tree-chevron ${isExpanded ? 'open' : ''}`}>
          {hasChildren ? '▸' : ''}
        </span>
        <span className="ref-tree-avatar">{node.username.charAt(0).toUpperCase()}</span>
        <span className="ref-tree-username">{node.username}</span>
        {node.role && node.role !== 'user' && (
          <span className="ref-tree-role-badge" title="Ce compte est admin/manager">{node.role}</span>
        )}
        <span className="ref-tree-rank">{RANK_ICONS[node.rank] || ''}</span>
        {node.capitalConfirmed && <span className="ref-tree-confirmed" title="Capital confirmé">✓</span>}
        <span className="ref-tree-counts">
          <span className="ref-tree-count-direct">{node.directCount}</span> direct
          {node.totalDescendants > node.directCount && (
            <span className="ref-tree-count-total"> · {node.totalDescendants} au total</span>
          )}
        </span>
        {isLoading && <span className="ref-tree-spinner" />}
      </div>

      {loadedChildren && (
        <div className={`ref-tree-children ${isExpanded ? 'open' : ''}`}>
          {loadedChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              childrenMap={childrenMap}
              loadingIds={loadingIds}
              onToggle={onToggle}
              highlightId={highlightId}
              nodeRefs={nodeRefs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ReferralTree = () => {
  const [roots, setRoots] = useState(null);
  const [childrenMap, setChildrenMap] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [highlightId, setHighlightId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const childrenMapRef = useRef({});
  const nodeRefs = useRef({});
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    api.get('/admin/referral-tree/roots').then((res) => {
      setRoots(res.data.nodes || []);
    });
  }, []);

  const loadChildren = useCallback(async (nodeId) => {
    if (childrenMapRef.current[nodeId]) return childrenMapRef.current[nodeId];
    setLoadingIds((prev) => new Set(prev).add(nodeId));
    try {
      const res = await api.get(`/admin/referral-tree/${nodeId}/children`);
      const nodes = res.data.nodes || [];
      childrenMapRef.current[nodeId] = nodes;
      setChildrenMap((prev) => ({ ...prev, [nodeId]: nodes }));
      return nodes;
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }
  }, []);

  const toggleNode = useCallback(async (nodeId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
    await loadChildren(nodeId);
  }, [loadChildren]);

  // Recherche avec debounce
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get('/admin/referral-tree/search', { params: { q: searchQuery.trim() } });
        setSearchResults(res.data.results || []);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  // Sélection d'un résultat: déplie chaque ancêtre puis scroll animé jusqu'au nœud
  const handleSelectResult = async (userId) => {
    setSearchResults([]);
    setSearchQuery('');

    const res = await api.get(`/admin/referral-tree/${userId}/path`);
    const path = res.data.path || [];
    if (path.length === 0) return;

    // Déplie chaque ancêtre (tous sauf le dernier, qui est le nœud cible)
    for (let i = 0; i < path.length - 1; i++) {
      const ancestorId = path[i].id;
      setExpandedIds((prev) => new Set(prev).add(ancestorId));
      // eslint-disable-next-line no-await-in-loop
      await loadChildren(ancestorId);
    }

    const targetId = path[path.length - 1].id;
    setHighlightId(targetId);

    // Laisse le DOM se mettre à jour (nœuds nouvellement dépliés) avant de scroller
    setTimeout(() => {
      const el = nodeRefs.current[targetId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);

    setTimeout(() => setHighlightId(null), 2600);
  };

  return (
    <div className="ref-tree-container">
      <div className="ref-tree-search">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Rechercher un utilisateur dans l'arbre..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchLoading && <span className="ref-tree-spinner" />}

        {searchResults.length > 0 && (
          <div className="ref-tree-search-results">
            {searchResults.map((r) => (
              <button key={r.id} className="ref-tree-search-result" onClick={() => handleSelectResult(r.id)}>
                <span className="ref-tree-avatar">{r.username.charAt(0).toUpperCase()}</span>
                {r.username}
                <span className="ref-tree-rank">{RANK_ICONS[r.rank] || ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ref-tree-scroll">
        {roots === null ? (
          <div className="ref-tree-loading">Chargement de l'arbre...</div>
        ) : roots.length === 0 ? (
          <div className="ref-tree-empty">Aucun utilisateur pour le moment</div>
        ) : (
          <div className="ref-tree-roots">
            {roots.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                expandedIds={expandedIds}
                childrenMap={childrenMap}
                loadingIds={loadingIds}
                onToggle={toggleNode}
                highlightId={highlightId}
                nodeRefs={nodeRefs}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralTree;
